// src/pages/MappingPage.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ReactSelect from "@/components/common/ReactSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import { apiCall } from "@/api/apiCall";
import { toast } from "sonner";
import {
  Database,
  ChevronRight,
  ChevronDown,
  Edit2,
  Check,
  X as XIcon,
  Save,
  Search,
  AlertCircle,
  RefreshCw,
  Tag,
  Layers,
  Hash,
  Menu,
  ArrowLeft,
  SlidersHorizontal,
  ChevronLeft,
} from "lucide-react";
import NoDataComponent from "@/components/common/NoDataComponent";

// ─── Build member edit map ─────────────────────────────────────────────────────
const buildEditMap = (dimensions) => {
  const map = {};
  const walk = (node) => {
    if (!node?.key) return;
    map[node.key] = {
      key: node.key,
      originalName: node.name || "",
      name: node.name || "",
      originalCaption: node.caption || node.name || "",
      caption: node.caption || node.name || "",
      originalFullName: node.fullName ?? null,
      fullName: node.fullName ?? null,
    };
    (node.children || []).forEach(walk);
  };
  dimensions.forEach((dim) => {
    (dim.tree || []).forEach(walk);
    (dim.unlinked || []).forEach(walk);
    (dim.members || []).forEach(walk);
  });
  return map;
};

// ─── Build dimension edit map ──────────────────────────────────────────────────
const buildDimEditMap = (dimensions) => {
  const map = {};
  dimensions.forEach((dim) => {
    map[dim.dimension] = {
      key: dim.dimension,
      originalName: dim.dimension,
      name: dim.dimension,
      originalCaption: dim.caption || dim.dimension,
      caption: dim.caption || dim.dimension,
    };
  });
  return map;
};

// ─── EditPanel (right sidebar) ────────────────────────────────────────────────
const MEMBER_EDIT_FIELDS = [
  { field: "name",     label: "Name",      placeholder: "Enter name"      },
  { field: "fullName", label: "Full Name", placeholder: "Enter full name" },
  { field: "caption",  label: "Caption",   placeholder: "Enter caption"   },
];

const DIM_EDIT_FIELDS = [
  { field: "name",    label: "Name",    placeholder: "Enter dimension name" },
  { field: "caption", label: "Caption", placeholder: "Enter caption"        },
];

const EditPanel = ({ entry, onEdit, fields = MEMBER_EDIT_FIELDS, headerIcon: HeaderIcon = Tag, headerLabel = "Member" }) => (
  <div className="flex flex-col h-full bg-card">
    <div className="flex items-center gap-2 px-4 py-3 border-b bg-primary/5 shrink-0">
      <div className="w-6 h-6 rounded-md bg-primary/20 flex items-center justify-center shrink-0">
        <HeaderIcon className="w-3.5 h-3.5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground/60 leading-none mb-0.5">
          {headerLabel}
        </p>
        <span className="text-[10px] font-mono text-muted-foreground truncate block select-all">
          {entry.key}
        </span>
      </div>
      <button
        onClick={() => onEdit(entry.key, "cancel")}
        className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-background/60"
      >
        <XIcon className="w-3.5 h-3.5" />
      </button>
    </div>

    <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
      {fields.map(({ field, label, placeholder }, idx) => (
        <div key={field} className="space-y-1.5">
          <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide block">
            {label}
          </label>
          <Input
            autoFocus={idx === 0}
            value={entry[field] ?? ""}
            onChange={(e) => {
              const val = field === "fullName" ? (e.target.value || null) : e.target.value;
              onEdit(entry.key, "change", { field, val });
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") onEdit(entry.key, "confirm");
              if (e.key === "Escape") onEdit(entry.key, "cancel");
            }}
            className="h-9 text-sm bg-background"
            placeholder={placeholder}
          />
        </div>
      ))}
    </div>

    <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/20 shrink-0">
      <span className="text-[10px] text-muted-foreground/50">
        Enter to save · Esc to cancel
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(entry.key, "cancel")}
          className="h-7 px-3 text-xs"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => onEdit(entry.key, "confirm")}
          className="h-7 px-3 text-xs"
        >
          <Check className="w-3 h-3 mr-1" /> Save
        </Button>
      </div>
    </div>
  </div>
);

// ─── MemberRow ─────────────────────────────────────────────────────────────────
const MemberRow = ({ node, entry, isDirty, isEditing, onEdit, indentPx, toggleExpand, hasChildren, isExpanded }) => {
  const displayCaption = entry.caption || node.caption || node.name;
  const displayName = entry.name || node.name;
  const showSecondary = displayName && displayName !== displayCaption;

  return (
    <div
      onClick={() => onEdit(node.key, "start")}
      data-member-key={node.key}
      className={`group flex items-center gap-1.5 rounded-lg py-2 transition-all duration-150 cursor-pointer ${
        isEditing
          ? "bg-primary/8 border border-primary/30"
          : isDirty
          ? "bg-amber-50 dark:bg-amber-950/30 border border-amber-200/70 dark:border-amber-800/50"
          : "hover:bg-muted/50 border border-transparent"
      }`}
      style={{ paddingLeft: `${indentPx}px`, paddingRight: "4px" }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); toggleExpand(); }}
        className="w-6 h-6 flex items-center justify-center shrink-0 rounded-md transition-colors hover:bg-muted active:scale-90"
      >
        {hasChildren ? (
          isExpanded
            ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
            : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
        ) : (
          <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/25" />
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-sm leading-snug truncate ${
            isDirty ? "text-amber-700 dark:text-amber-400 font-medium" : "text-foreground"
          }`}>
            {displayCaption}
          </span>
          {isDirty && (
            <span className="inline-flex items-center gap-1 text-[9px] font-bold text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/60 px-1.5 py-0.5 rounded-full shrink-0 uppercase tracking-wide">
              edited
            </span>
          )}
        </div>
        {showSecondary && (
          <span className="text-[10px] text-muted-foreground/40 font-mono truncate block leading-tight mt-0.5">
            {displayName}
          </span>
        )}
      </div>

      <div className="p-2 rounded-lg opacity-0 group-hover:opacity-100 sm:opacity-20 sm:group-hover:opacity-100 transition-all duration-150 text-muted-foreground shrink-0">
        <Edit2 className="w-3.5 h-3.5" />
      </div>
    </div>
  );
};

// ─── TreeNode ──────────────────────────────────────────────────────────────────
const TreeNode = ({ node, depth = 0, editMap, onEdit, pendingKeys, searchTerm }) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;
  const entry = editMap[node.key] || {};
  const isDirty = pendingKeys.has(node.key);
  const isEditing = entry._editing;

  const forceExpand = searchTerm && hasChildren;
  const isExpanded = forceExpand || expanded;

  const matchesSearch = !searchTerm || (
    (node.caption || "").toLowerCase().includes(searchTerm) ||
    (node.name || "").toLowerCase().includes(searchTerm) ||
    (entry.caption || "").toLowerCase().includes(searchTerm)
  );

  const childMatchesSearch = useMemo(() => {
    if (!searchTerm || !hasChildren) return false;
    const checkChildren = (children) =>
      children.some(c =>
        (c.caption || "").toLowerCase().includes(searchTerm) ||
        (c.name || "").toLowerCase().includes(searchTerm) ||
        checkChildren(c.children || [])
      );
    return checkChildren(node.children);
  }, [searchTerm, node.children, hasChildren]);

  if (searchTerm && !matchesSearch && !childMatchesSearch) return null;

  return (
    <div>
      <MemberRow
        node={node}
        entry={entry}
        isDirty={isDirty}
        isEditing={isEditing}
        onEdit={onEdit}
        indentPx={depth * 16 + 6}
        toggleExpand={() => setExpanded(!expanded)}
        hasChildren={hasChildren}
        isExpanded={isExpanded}
      />
      {hasChildren && isExpanded && (
        <div className="relative">
          <div
            className="absolute top-0 bottom-0 w-px bg-border/30"
            style={{ left: `${depth * 16 + 18}px` }}
          />
          {node.children.map((child, i) => (
            <TreeNode
              key={child.key || i}
              node={child}
              depth={depth + 1}
              editMap={editMap}
              onEdit={onEdit}
              pendingKeys={pendingKeys}
              searchTerm={searchTerm}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── DimensionList ─────────────────────────────────────────────────────────────
const DimensionList = ({
  dimensions,
  selectedDim,
  setSelectedDim,
  setSearchTerm,
  pendingByDim,
  pendingDimKeys,
  dimMemberCounts,
  onSelect,
  isCollapsed,
  onToggleCollapse,
  onDimEdit,
  activeDimEditKey,
}) => (
  <div className={`flex flex-col h-full border-r bg-card transition-all duration-300 ${isCollapsed ? "w-12" : "w-56 lg:w-60 xl:w-64"}`}>
    {/* Header with collapse button */}
    <div className="px-2 py-2.5 border-b flex items-center justify-between shrink-0">
      {!isCollapsed ? (
        <>
          <div className="flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Dimensions
            </span>
            <span className="text-[11px] font-medium text-muted-foreground bg-muted rounded-full px-1.5 py-0.5 leading-none ml-1">
              {dimensions.length}
            </span>
          </div>
          <button
            onClick={onToggleCollapse}
            className="p-1 rounded-md hover:bg-muted transition-colors"
            title="Collapse sidebar"
          >
            <ChevronLeft className="w-4 h-4 text-muted-foreground" />
          </button>
        </>
      ) : (
        <button
          onClick={onToggleCollapse}
          className="w-full flex justify-center p-1 rounded-md hover:bg-muted transition-colors"
          title="Expand sidebar"
        >
          <Menu className="w-4 h-4 text-muted-foreground" />
        </button>
      )}
    </div>

    <div className="flex-1 overflow-y-auto py-1">
      {dimensions.map((dim) => {
        const isActive = selectedDim === dim.dimension;
        const isDimEditing = activeDimEditKey === dim.dimension;
        const dirtyCount = pendingByDim[dim.dimension] || 0;
        const isDimDirty = pendingDimKeys.has(dim.dimension);
        const memberCount = dimMemberCounts[dim.dimension] || 0;
        const displayName = dim.dimension.replace(/_/g, " ");

        return (
          <div
            key={dim.dimension}
            role="button"
            onClick={() => {
              setSelectedDim(dim.dimension);
              setSearchTerm("");
              onSelect?.();
            }}
            className={`w-full text-left transition-colors text-sm group ${
              isActive
                ? "bg-primary/8 text-primary"
                : isDimEditing
                ? "bg-primary/5"
                : "hover:bg-muted/50 text-foreground"
            } ${isCollapsed ? "px-2 py-3 flex justify-center cursor-pointer" : "px-3 py-2.5 cursor-pointer"}`}
            title={isCollapsed ? displayName : undefined}
          >
            {isCollapsed ? (
              <div className="relative">
                <Layers className="w-4 h-4" />
                {(dirtyCount > 0 || isDimDirty) && (
                  <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-amber-500 text-white text-[8px] font-bold flex items-center justify-center">
                    {dirtyCount + (isDimDirty ? 1 : 0)}
                  </span>
                )}
              </div>
            ) : (
              <div className="flex items-start gap-2.5">
                <div className={`w-0.5 self-stretch rounded-full shrink-0 transition-all mt-0.5 ${
                  isActive ? "bg-primary" : "bg-transparent group-hover:bg-muted-foreground/20"
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1">
                    <span className={`text-sm leading-snug truncate ${isActive ? "font-semibold" : "font-normal"}`}>
                      {displayName}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      {isDimDirty && (
                        <span className="text-[9px] font-bold px-1 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 leading-none">
                          dim
                        </span>
                      )}
                      {dirtyCount > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/60 text-amber-700 dark:text-amber-300 min-w-[18px] text-center leading-none">
                          {dirtyCount}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium leading-none ${
                      isActive ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                    }`}>
                      {dim.mode}
                    </span>
                    <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground/60">
                      <Hash className="w-2.5 h-2.5" />
                      {memberCount}
                    </span>
                  </div>
                </div>
                {/* Edit dimension button — visible on hover */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDimEdit(dim.dimension, "start");
                  }}
                  title="Edit dimension"
                  className={`p-1 rounded-md transition-all shrink-0 mt-0.5 ${
                    isDimEditing
                      ? "opacity-100 bg-primary/10 text-primary"
                      : "opacity-0 group-hover:opacity-100 hover:bg-muted text-muted-foreground"
                  }`}
                >
                  <Edit2 className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

// ─── UnsavedDialog ────────────────────────────────────────────────────────────
const UnsavedDialog = ({ open, onSave, onDiscard, onCancel }) => {
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card rounded-xl shadow-2xl border border-border p-5 w-[300px] max-w-[90vw]">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <p className="text-sm font-semibold">Unsaved Changes</p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              This item has unsaved changes. Save before switching?
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 justify-end">
          <Button variant="ghost" size="sm" onClick={onCancel} className="h-7 text-xs">
            Keep Editing
          </Button>
          <Button variant="outline" size="sm" onClick={onDiscard} className="h-7 text-xs">
            Discard
          </Button>
          <Button size="sm" onClick={onSave} className="h-7 text-xs">
            <Save className="w-3 h-3 mr-1" /> Save
          </Button>
        </div>
      </div>
    </>
  );
};

// ─── ChangesPopover ───────────────────────────────────────────────────────────
const FIELD_LABELS = { name: "Name", fullName: "Full Name", caption: "Caption" };
const MEMBER_FIELD_PAIRS = [
  ["name",     "originalName"    ],
  ["fullName", "originalFullName"],
  ["caption",  "originalCaption" ],
];
const DIM_FIELD_PAIRS = [
  ["name",    "originalName"   ],
  ["caption", "originalCaption"],
];

const ChangesPopover = ({ pendingKeys, editMap, pendingDimKeys, dimEditMap, onClose, onSelect, onDimSelect }) => {
  const totalCount = pendingKeys.size + pendingDimKeys.size;
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute top-full right-0 mt-2 z-50 w-80 bg-card border border-border rounded-xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2.5 border-b bg-muted/30 shrink-0">
          <span className="text-xs font-semibold">
            {totalCount} Unsaved Change{totalCount > 1 ? "s" : ""}
          </span>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-muted text-muted-foreground">
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="max-h-72 overflow-y-auto divide-y divide-border/50">
          {/* Dimension-level changes */}
          {[...pendingDimKeys].map((dimKey) => {
            const entry = dimEditMap[dimKey];
            if (!entry) return null;
            const changedFields = DIM_FIELD_PAIRS.filter(
              ([cur, orig]) => (entry[cur] ?? null) !== (entry[orig] ?? null)
            );
            const displayName = entry.caption || entry.name || dimKey;
            return (
              <div
                key={`dim:${dimKey}`}
                onClick={() => onDimSelect(dimKey)}
                className="px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                    Dimension
                  </span>
                  <p className="text-[11px] font-semibold text-foreground truncate">{displayName}</p>
                </div>
                <div className="space-y-1.5">
                  {changedFields.map(([cur, orig]) => (
                    <div key={cur} className="flex items-start gap-1.5 text-[10px]">
                      <span className="text-muted-foreground w-[52px] shrink-0 pt-px">{FIELD_LABELS[cur]}</span>
                      <div className="flex items-center gap-1 flex-wrap min-w-0">
                        <span className="px-1.5 py-px rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 line-through font-mono truncate max-w-[90px]">
                          {entry[orig] || "—"}
                        </span>
                        <span className="text-muted-foreground">→</span>
                        <span className="px-1.5 py-px rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-mono truncate max-w-[90px]">
                          {entry[cur] || "—"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {/* Member-level changes */}
          {[...pendingKeys].map((key) => {
            const entry = editMap[key];
            if (!entry) return null;
            const changedFields = MEMBER_FIELD_PAIRS.filter(
              ([cur, orig]) => (entry[cur] ?? null) !== (entry[orig] ?? null)
            );
            const displayName = entry.caption || entry.name || key;
            return (
              <div
                key={key}
                onClick={() => onSelect(key)}
                className="px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <p className="text-[11px] font-semibold text-foreground truncate mb-1.5">{displayName}</p>
                <div className="space-y-1.5">
                  {changedFields.map(([cur, orig]) => (
                    <div key={cur} className="flex items-start gap-1.5 text-[10px]">
                      <span className="text-muted-foreground w-[52px] shrink-0 pt-px">{FIELD_LABELS[cur]}</span>
                      <div className="flex items-center gap-1 flex-wrap min-w-0">
                        <span className="px-1.5 py-px rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 line-through font-mono truncate max-w-[90px]">
                          {entry[orig] || "—"}
                        </span>
                        <span className="text-muted-foreground">→</span>
                        <span className="px-1.5 py-px rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-mono truncate max-w-[90px]">
                          {entry[cur] || "—"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

// ─── Bottom Sheet ──────────────────────────────────────────────────────────────
const BottomSheet = ({ open, onClose, title, children }) => {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 bg-card rounded-t-2xl shadow-2xl border-t border-border transition-transform duration-300 ease-out flex flex-col`}
        style={{
          transform: open ? "translateY(0)" : "translateY(100%)",
          maxHeight: "75vh",
        }}
      >
        <div className="flex items-center justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/25" />
        </div>
        <div className="flex items-center justify-between px-4 pb-2 pt-1 border-b shrink-0">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-semibold">{title}</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
            <XIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    </>
  );
};

// ─── RightDrawer ──────────────────────────────────────────────────────────────
const RightDrawer = ({ open, onClose, children }) => {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 sm:hidden ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />
      <div
        className="fixed top-0 right-0 bottom-0 z-50 w-[85vw] max-w-sm bg-card shadow-2xl border-l border-border transition-transform duration-300 ease-out flex flex-col sm:hidden"
        style={{ transform: open ? "translateX(0)" : "translateX(100%)" }}
      >
        {children}
      </div>
    </>
  );
};

// ─── MemberTreeContent ─────────────────────────────────────────────────────────
const MemberTreeContent = ({ dimNodes, currentDimData, normalizedSearch, editMap, handleEdit, pendingKeys }) => {
  if (dimNodes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center gap-2 py-12">
        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
          <Layers className="w-5 h-5 text-muted-foreground/50" />
        </div>
        <p className="text-sm text-muted-foreground">No members found.</p>
      </div>
    );
  }

  if (currentDimData?.mode === "leaf") {
    return (
      <div className="space-y-0.5 pb-20 sm:pb-2">
        {dimNodes
          .filter((n) => !normalizedSearch || (n.caption || n.name || "").toLowerCase().includes(normalizedSearch))
          .map((node, i) => {
            const entry = editMap[node.key] || {};
            return (
              <MemberRow
                key={node.key || i}
                node={node}
                entry={entry}
                isDirty={pendingKeys.has(node.key)}
                isEditing={!!entry._editing}
                onEdit={handleEdit}
                indentPx={6}
                toggleExpand={() => {}}
                hasChildren={false}
                isExpanded={false}
              />
            );
          })}
      </div>
    );
  }

  return (
    <div className="space-y-0.5 pb-20 sm:pb-2">
      {dimNodes.map((node, i) => (
        <TreeNode
          key={node.key || i}
          node={node}
          depth={0}
          editMap={editMap}
          onEdit={handleEdit}
          pendingKeys={pendingKeys}
          searchTerm={normalizedSearch}
        />
      ))}
    </div>
  );
};

// ─── MAIN PAGE ─────────────────────────────────────────────────────────────────
const MappingPage = () => {
  const [cubesLoading, setCubesLoading] = useState(true);
  const [cubesError, setCubesError] = useState(null);
  const [allCubes, setAllCubes] = useState([]);
  const [selectedCube, setSelectedCube] = useState(null);

  const [metaLoading, setMetaLoading] = useState(false);
  const [metaError, setMetaError] = useState(null);
  const [dimensions, setDimensions] = useState([]);
  const [selectedDim, setSelectedDim] = useState(null);

  // Member edits
  const [editMap, setEditMap] = useState({});
  const [pendingKeys, setPendingKeys] = useState(new Set());

  // Dimension-level edits
  const [dimEditMap, setDimEditMap] = useState({});
  const [pendingDimKeys, setPendingDimKeys] = useState(new Set());
  const [activeDimEditKey, setActiveDimEditKey] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [saving, setSaving] = useState(false);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [dimSheetOpen, setDimSheetOpen] = useState(false);

  // confirmSwitch: { fromKey, toKey, type: "member" | "dim" }
  const [confirmSwitch, setConfirmSwitch] = useState(null);
  const [showChanges, setShowChanges] = useState(false);

  const hasFetchedRef = useRef(false);
  const searchRef = useRef(null);
  const currentEditingKeyRef = useRef(null);
  const editMapRef = useRef({});
  const dimEditMapRef = useRef({});

  useEffect(() => { editMapRef.current = editMap; }, [editMap]);
  useEffect(() => { dimEditMapRef.current = dimEditMap; }, [dimEditMap]);

  // Fetch cubes
  const fetchCubes = async () => {
    setCubesLoading(true);
    setCubesError(null);
    try {
      const response = await apiCall("get", "/tm1/metadata");
      const items = response?.data || [];
      const cubes = items.flatMap((serverItem) => {
        const server = serverItem.server;
        const cubeEntries = serverItem.data?.value || [];
        return cubeEntries.map((entry) => {
          if (/^[{}]/.test(entry?.Name)) return null;
          const name = (entry?.Name || "").replace(/_/g, " ").replace(/[{}]/g, "").trim();
          if (!name) return null;
          return { value: entry?.Name, label: `${name} (${server})`, server };
        }).filter(Boolean);
      });
      setAllCubes(cubes);
    } catch (err) {
      setCubesError(`Failed to load cubes. ${err}`);
    } finally {
      setCubesLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchCubes();
  }, []);

  // Fetch metadata
  const fetchMeta = async (cube) => {
    setMetaLoading(true);
    setMetaError(null);
    setDimensions([]);
    setSelectedDim(null);
    setEditMap({});
    setPendingKeys(new Set());
    setDimEditMap({});
    setPendingDimKeys(new Set());
    setActiveDimEditKey(null);
    setSearchTerm("");
    try {
      const res = await apiCall(
        "get",
        `/getCubeMetaWithEdges?server=${encodeURIComponent(cube.server)}&cube=${encodeURIComponent(cube.value)}`,
        null, null, false, false
      );
      if (res.success === true) {
        const dims = res.dimensions || [];
        setDimensions(dims);
        setEditMap(buildEditMap(dims));
        setDimEditMap(buildDimEditMap(dims));
        if (dims.length > 0) setSelectedDim(dims[0].dimension);
      } else {
        setMetaError(res.message || "Failed to load metadata");
      }
    } catch (err) {
      setMetaError(String(err));
    } finally {
      setMetaLoading(false);
    }
  };

  const handleCubeChange = (cube) => {
    setSelectedCube(cube);
    if (cube) fetchMeta(cube);
  };

  // ── Member edit handler ──────────────────────────────────────────────────────
  const handleEdit = useCallback((key, action, value) => {
    if (action === "start") {
      // Close any open dim edit first (no unsaved check needed — dim edit panel will handle its own)
      setActiveDimEditKey(null);
      setDimEditMap((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => {
          if (next[k]._editing) next[k] = { ...next[k], _editing: false };
        });
        return next;
      });

      const fromKey = currentEditingKeyRef.current;
      if (fromKey && fromKey !== key) {
        const fromEntry = editMapRef.current[fromKey];
        const hasChanges = fromEntry && (
          fromEntry.name !== fromEntry.originalName ||
          fromEntry.caption !== fromEntry.originalCaption ||
          (fromEntry.fullName || null) !== (fromEntry.originalFullName || null)
        );
        if (hasChanges) {
          setConfirmSwitch({ fromKey, toKey: key, type: "member" });
          return;
        }
        currentEditingKeyRef.current = key;
        setEditMap((prev) => ({
          ...prev,
          [fromKey]: { ...prev[fromKey], _editing: false },
          [key]: { ...(prev[key] || {}), _editing: true },
        }));
        return;
      }
      currentEditingKeyRef.current = key;
      setEditMap((prev) => {
        const entry = prev[key];
        if (!entry) return prev;
        return { ...prev, [key]: { ...entry, _editing: true } };
      });
      return;
    }

    setEditMap((prev) => {
      const entry = prev[key];
      if (!entry) return prev;
      if (action === "change") {
        return { ...prev, [key]: { ...entry, [value.field]: value.val } };
      }
      if (action === "confirm") {
        const newEntry = { ...entry, _editing: false };
        currentEditingKeyRef.current = null;
        setPendingKeys((pk) => {
          const next = new Set(pk);
          const isDirty =
            newEntry.name !== newEntry.originalName ||
            newEntry.caption !== newEntry.originalCaption ||
            (newEntry.fullName || null) !== (newEntry.originalFullName || null);
          if (isDirty) next.add(key);
          else next.delete(key);
          return next;
        });
        return { ...prev, [key]: newEntry };
      }
      if (action === "cancel") {
        currentEditingKeyRef.current = null;
        return {
          ...prev,
          [key]: {
            ...entry,
            name: entry.originalName,
            caption: entry.originalCaption,
            fullName: entry.originalFullName,
            _editing: false,
          },
        };
      }
      return prev;
    });
  }, []);

  // ── Dimension edit handler ───────────────────────────────────────────────────
  const handleDimEdit = useCallback((key, action, value) => {
    if (action === "start") {
      // Close any open member edit first
      if (currentEditingKeyRef.current) {
        setEditMap((prev) => ({
          ...prev,
          [currentEditingKeyRef.current]: { ...prev[currentEditingKeyRef.current], _editing: false },
        }));
        currentEditingKeyRef.current = null;
      }

      // Check if another dim is already being edited with unsaved changes
      if (activeDimEditKey && activeDimEditKey !== key) {
        const fromEntry = dimEditMapRef.current[activeDimEditKey];
        const hasChanges = fromEntry && (
          fromEntry.name !== fromEntry.originalName ||
          fromEntry.caption !== fromEntry.originalCaption
        );
        if (hasChanges) {
          setConfirmSwitch({ fromKey: activeDimEditKey, toKey: key, type: "dim" });
          return;
        }
        // No changes — just switch without dialog
        setDimEditMap((prev) => ({
          ...prev,
          [activeDimEditKey]: { ...prev[activeDimEditKey], _editing: false },
          [key]: { ...(prev[key] || {}), _editing: true },
        }));
        setActiveDimEditKey(key);
        return;
      }

      setActiveDimEditKey(key);
      setDimEditMap((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), _editing: true } }));
      return;
    }

    setDimEditMap((prev) => {
      const entry = prev[key];
      if (!entry) return prev;
      if (action === "change") {
        return { ...prev, [key]: { ...entry, [value.field]: value.val } };
      }
      if (action === "confirm") {
        const newEntry = { ...entry, _editing: false };
        setActiveDimEditKey(null);
        setPendingDimKeys((pk) => {
          const next = new Set(pk);
          const isDirty =
            newEntry.name !== newEntry.originalName ||
            newEntry.caption !== newEntry.originalCaption;
          if (isDirty) next.add(key);
          else next.delete(key);
          return next;
        });
        return { ...prev, [key]: newEntry };
      }
      if (action === "cancel") {
        setActiveDimEditKey(null);
        return {
          ...prev,
          [key]: {
            ...entry,
            name: entry.originalName,
            caption: entry.originalCaption,
            _editing: false,
          },
        };
      }
      return prev;
    });
  }, [activeDimEditKey]);

  const handleSelectFromChanges = useCallback((key) => {
    setShowChanges(false);
    const dimMatch = key.match(/^\[([^\]]+)\]/);
    const dimName = dimMatch?.[1];
    if (dimName) setSelectedDim(dimName);
    setTimeout(() => {
      handleEdit(key, "start");
      setTimeout(() => {
        const el = document.querySelector(`[data-member-key="${key.replace(/"/g, '\\"')}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    }, dimName ? 50 : 0);
  }, [handleEdit]);

  const handleDimSelectFromChanges = useCallback((dimKey) => {
    setShowChanges(false);
    setSelectedDim(dimKey);
    setTimeout(() => handleDimEdit(dimKey, "start"), 50);
  }, [handleDimEdit]);

  // ── Confirm switch handlers (member + dim) ───────────────────────────────────
  const handleConfirmSave = () => {
    if (!confirmSwitch) return;
    const { fromKey, toKey, type } = confirmSwitch;
    setConfirmSwitch(null);

    if (type === "dim") {
      const fromEntry = dimEditMapRef.current[fromKey];
      if (fromEntry) {
        const isDirty =
          fromEntry.name !== fromEntry.originalName ||
          fromEntry.caption !== fromEntry.originalCaption;
        setPendingDimKeys((pk) => {
          const next = new Set(pk);
          if (isDirty) next.add(fromKey);
          else next.delete(fromKey);
          return next;
        });
      }
      setDimEditMap((prev) => ({
        ...prev,
        [fromKey]: { ...prev[fromKey], _editing: false },
        [toKey]: { ...(prev[toKey] || {}), _editing: true },
      }));
      setActiveDimEditKey(toKey);
      return;
    }

    // type === "member"
    const fromEntry = editMapRef.current[fromKey];
    if (fromEntry) {
      const isDirty =
        fromEntry.name !== fromEntry.originalName ||
        fromEntry.caption !== fromEntry.originalCaption ||
        (fromEntry.fullName || null) !== (fromEntry.originalFullName || null);
      setPendingKeys((pk) => {
        const next = new Set(pk);
        if (isDirty) next.add(fromKey);
        else next.delete(fromKey);
        return next;
      });
    }
    currentEditingKeyRef.current = toKey;
    setEditMap((prev) => ({
      ...prev,
      [fromKey]: { ...prev[fromKey], _editing: false },
      [toKey]: { ...(prev[toKey] || {}), _editing: true },
    }));
  };

  const handleConfirmDiscard = () => {
    if (!confirmSwitch) return;
    const { fromKey, toKey, type } = confirmSwitch;
    setConfirmSwitch(null);

    if (type === "dim") {
      setDimEditMap((prev) => ({
        ...prev,
        [fromKey]: {
          ...prev[fromKey],
          name: prev[fromKey].originalName,
          caption: prev[fromKey].originalCaption,
          _editing: false,
        },
        [toKey]: { ...(prev[toKey] || {}), _editing: true },
      }));
      setActiveDimEditKey(toKey);
      return;
    }

    // type === "member"
    currentEditingKeyRef.current = toKey;
    setEditMap((prev) => ({
      ...prev,
      [fromKey]: {
        ...prev[fromKey],
        name: prev[fromKey].originalName,
        caption: prev[fromKey].originalCaption,
        fullName: prev[fromKey].originalFullName,
        _editing: false,
      },
      [toKey]: { ...(prev[toKey] || {}), _editing: true },
    }));
  };

  const handleDiscardAll = () => {
    setEditMap((prev) => {
      const next = { ...prev };
      pendingKeys.forEach((key) => {
        if (next[key]) {
          next[key] = {
            ...next[key],
            name: next[key].originalName,
            caption: next[key].originalCaption,
            fullName: next[key].originalFullName,
          };
        }
      });
      return next;
    });
    setPendingKeys(new Set());
    setDimEditMap((prev) => {
      const next = { ...prev };
      pendingDimKeys.forEach((key) => {
        if (next[key]) {
          next[key] = {
            ...next[key],
            name: next[key].originalName,
            caption: next[key].originalCaption,
          };
        }
      });
      return next;
    });
    setPendingDimKeys(new Set());
    setActiveDimEditKey(null);
    toast.info("All changes discarded");
  };

const handleSave = async () => {
  const hasMemberChanges = pendingKeys.size > 0;
  const hasDimChanges = pendingDimKeys.size > 0;
  if ((!hasMemberChanges && !hasDimChanges) || !selectedCube) return;

  setSaving(true);
  try {
    // Build complete payload
    const members = [];
    const dimensions = [];

    // Collect all member updates
    pendingKeys.forEach((key) => {
      const entry = editMap[key];
      if (!entry) return;
      members.push({
        key: entry.key,
        name: entry.name,
        caption: entry.caption,
        fullName: entry.fullName,
      });
    });

    // Collect all dimension updates
    pendingDimKeys.forEach((dimension) => {
      const dimEntry = dimEditMap[dimension];
      if (!dimEntry) return;
      dimensions.push({
        dimension: dimension,
        name: dimEntry.name,
        caption: dimEntry.caption,
      });
    });

    // Complete payload
    const payload = {
      server: selectedCube.server,
      cube: selectedCube.value,
      members: members,
      dimensions: dimensions,
    };

    console.log("Saving payload:", JSON.stringify(payload, null, 2));

    const response = await apiCall("put", "/tm1/mapping", payload, null, false, false);

    if (response?.success === true) {
      const totalCount = pendingKeys.size + pendingDimKeys.size;
      toast.success(`${totalCount} change${totalCount > 1 ? "s" : ""} saved successfully`);

      // Reset member originals
      setEditMap((prev) => {
        const next = { ...prev };
        pendingKeys.forEach((key) => {
          if (next[key]) {
            next[key] = {
              ...next[key],
              originalName: next[key].name,
              originalCaption: next[key].caption,
              originalFullName: next[key].fullName,
            };
          }
        });
        return next;
      });
      setPendingKeys(new Set());

      // Reset dim originals
      setDimEditMap((prev) => {
        const next = { ...prev };
        pendingDimKeys.forEach((key) => {
          if (next[key]) {
            next[key] = {
              ...next[key],
              originalName: next[key].name,
              originalCaption: next[key].caption,
            };
          }
        });
        return next;
      });
      setPendingDimKeys(new Set());
    } else {
      toast.error(response?.message || "Save failed. Please try again.");
    }
  } catch (err) {
    toast.error(`Save failed: ${err}`);
  } finally {
    setSaving(false);
  }
};

  // Derived data
  const currentDimData = useMemo(
    () => dimensions.find((d) => d.dimension === selectedDim),
    [dimensions, selectedDim]
  );

  const dimNodes = useMemo(() => {
    if (!currentDimData) return [];
    if (currentDimData.mode === "leaf") return currentDimData.members || [];
    return [
      ...(currentDimData.tree || []),
      ...(currentDimData.unlinked || []).map((u) => ({ ...u, _unlinked: true })),
    ];
  }, [currentDimData]);

  const normalizedSearch = searchTerm.toLowerCase().trim();

  const pendingByDim = useMemo(() => {
    const counts = {};
    pendingKeys.forEach((key) => {
      const dimMatch = key.match(/^\[([^\]]+)\]/);
      const dimName = dimMatch?.[1];
      if (dimName) counts[dimName] = (counts[dimName] || 0) + 1;
    });
    return counts;
  }, [pendingKeys]);

  const dimMemberCounts = useMemo(() => {
    const counts = {};
    const countTree = (nodes) =>
      (nodes || []).reduce((acc, n) => acc + 1 + countTree(n.children || []), 0);
    dimensions.forEach((dim) => {
      counts[dim.dimension] =
        countTree(dim.tree || []) +
        (dim.unlinked || []).length +
        (dim.members || []).length;
    });
    return counts;
  }, [dimensions]);

  const activeEditKey = useMemo(
    () => Object.keys(editMap).find((k) => editMap[k]?._editing) || null,
    [editMap]
  );

  // Determine what to show in the right panel
  const rightPanelType = activeDimEditKey ? "dim" : activeEditKey ? "member" : null;
  const rightPanelOpen = !!rightPanelType;

  const selectedDimDisplay = selectedDim?.replace(/_/g, " ");
  const totalPendingCount = pendingKeys.size + pendingDimKeys.size;

  // Render
  if (cubesLoading) return <LoadingComponent fullScreen />;
  if (cubesError && allCubes.length === 0) return <ErrorState error={cubesError} onRetry={fetchCubes} />;

  const hasDimensions = dimensions.length > 0 && !metaLoading && !metaError;

  const renderRightPanel = () => {
    if (rightPanelType === "dim" && dimEditMap[activeDimEditKey]) {
      return (
        <EditPanel
          entry={dimEditMap[activeDimEditKey]}
          onEdit={handleDimEdit}
          fields={DIM_EDIT_FIELDS}
          headerIcon={Layers}
          headerLabel="Dimension"
        />
      );
    }
    if (rightPanelType === "member" && editMap[activeEditKey]) {
      return (
        <EditPanel
          entry={editMap[activeEditKey]}
          onEdit={handleEdit}
          fields={MEMBER_EDIT_FIELDS}
          headerIcon={Tag}
          headerLabel="Member"
        />
      );
    }
    return null;
  };

  const handleRightPanelClose = () => {
    if (rightPanelType === "dim") handleDimEdit(activeDimEditKey, "cancel");
    else if (rightPanelType === "member") handleEdit(activeEditKey, "cancel");
  };

  return (
    <div className="flex flex-col bg-background" style={{ height: "100dvh" }}>

      {/* Top Bar */}
      <div className="flex items-center border-b bg-card shrink-0 h-14 px-3 sm:px-4 gap-2 sm:gap-4">
        <div className={`items-center gap-2 shrink-0 ${selectedCube ? "hidden sm:flex" : "flex"}`}>
          <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Tag className="w-4 h-4 text-primary" />
          </div>
          <div className="hidden md:block">
            <p className="text-sm font-semibold leading-none">Mapping Editor</p>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-none">TM1 member labels</p>
          </div>
        </div>

        <div className="hidden sm:block h-6 w-px bg-border shrink-0" />

        <div className="flex-1 sm:flex-none sm:w-[300px] md:w-[340px]">
          <ReactSelect
            options={allCubes}
            value={selectedCube}
            onChange={handleCubeChange}
            placeholder="Select a cube..."
            isLoading={cubesLoading}
          />
        </div>

        {hasDimensions && (
          <button
            onClick={() => setDimSheetOpen(true)}
            className="sm:hidden relative p-2 rounded-lg border border-border hover:bg-muted text-muted-foreground active:scale-90 transition-all shrink-0"
          >
            <SlidersHorizontal className="w-4 h-4" />
            {Object.keys(pendingByDim).length > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">
                {Object.keys(pendingByDim).length}
              </span>
            )}
          </button>
        )}

        <div className="hidden sm:block flex-1" />

        {totalPendingCount > 0 && (
          <div className="relative hidden sm:flex items-center gap-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 rounded-lg px-3 py-1.5 shrink-0">
            <button
              onClick={() => setShowChanges((v) => !v)}
              className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
            >
              <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-xs font-medium text-amber-700 dark:text-amber-400 whitespace-nowrap underline-offset-2 hover:underline">
                {totalPendingCount} unsaved
              </span>
            </button>
            <div className="h-4 w-px bg-amber-200 dark:bg-amber-700 mx-0.5" />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDiscardAll}
              disabled={saving}
              className="h-6 px-2 text-xs text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50"
            >
              <XIcon className="w-3 h-3 mr-1" /> Discard
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="h-6 px-2.5 text-xs bg-amber-600 hover:bg-amber-700 text-white border-0"
            >
              {saving
                ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                : <Save className="w-3 h-3 mr-1" />
              }
              Save
            </Button>
            {showChanges && (
              <ChangesPopover
                pendingKeys={pendingKeys}
                editMap={editMap}
                pendingDimKeys={pendingDimKeys}
                dimEditMap={dimEditMap}
                onClose={() => setShowChanges(false)}
                onSelect={handleSelectFromChanges}
                onDimSelect={handleDimSelectFromChanges}
              />
            )}
          </div>
        )}
      </div>

      {/* Body */}
      {!selectedCube ? (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-xs">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-muted flex items-center justify-center">
              <Database className="w-8 h-8 text-muted-foreground/60" />
            </div>
            <h3 className="font-semibold text-base mb-2">Select a Cube</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Choose a cube from the dropdown above to browse and edit dimension member labels.
            </p>
          </div>
        </div>
      ) : metaLoading ? (
        <LoadingComponent />
      ) : metaError ? (
        <ErrorState error={metaError} onRetry={() => fetchMeta(selectedCube)} />
      ) : dimensions.length === 0 ? (
        <NoDataComponent message="No dimensions found for this cube." />
      ) : (
        <>
          {/* Desktop Layout */}
          <div className="hidden sm:flex flex-1 min-h-0">
            <DimensionList
              dimensions={dimensions}
              selectedDim={selectedDim}
              setSelectedDim={setSelectedDim}
              setSearchTerm={setSearchTerm}
              pendingByDim={pendingByDim}
              pendingDimKeys={pendingDimKeys}
              dimMemberCounts={dimMemberCounts}
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              onDimEdit={handleDimEdit}
              activeDimEditKey={activeDimEditKey}
            />

            {/* Member Tree */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="px-4 py-2.5 border-b bg-card flex items-center gap-3 shrink-0">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-sm font-semibold truncate leading-snug">
                      {selectedDimDisplay}
                    </h2>
                    <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium leading-none shrink-0">
                      {currentDimData?.mode}
                    </span>
                    {pendingDimKeys.has(selectedDim) && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-medium leading-none shrink-0">
                        dim edited
                      </span>
                    )}
                    {pendingByDim[selectedDim] > 0 && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 font-medium leading-none shrink-0">
                        {pendingByDim[selectedDim]} edited
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-none">
                    {dimNodes.length} member{dimNodes.length !== 1 ? "s" : ""}
                  </p>
                </div>

                {/* Edit dimension button in header */}
                <button
                  onClick={() => handleDimEdit(selectedDim, "start")}
                  title="Edit dimension"
                  className={`p-1.5 rounded-lg border transition-all shrink-0 ${
                    activeDimEditKey === selectedDim
                      ? "bg-primary/10 border-primary/30 text-primary"
                      : "border-border hover:bg-muted text-muted-foreground"
                  }`}
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </button>

                <div className="relative w-[200px] lg:w-[240px] shrink-0">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                  <Input
                    ref={searchRef}
                    placeholder="Search members…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 h-8 text-sm pr-8"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <XIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-2 py-1.5">
                <MemberTreeContent
                  dimNodes={dimNodes}
                  currentDimData={currentDimData}
                  normalizedSearch={normalizedSearch}
                  editMap={editMap}
                  handleEdit={handleEdit}
                  pendingKeys={pendingKeys}
                />
              </div>

              {totalPendingCount > 0 && (
                <div className="px-4 py-2 border-t bg-amber-50 dark:bg-amber-950/15 flex items-center gap-2 shrink-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse shrink-0" />
                  <p className="text-xs text-amber-700 dark:text-amber-400 flex-1">
                    <span className="font-semibold">{totalPendingCount}</span>{" "}
                    pending change{totalPendingCount > 1 ? "s" : ""} — save from the top bar to persist
                  </p>
                </div>
              )}
            </div>

            {/* Right Panel — member or dimension edit */}
            <div
              className={`border-l flex flex-col shrink-0 overflow-hidden transition-all duration-200 ease-in-out ${
                rightPanelOpen ? "w-72 xl:w-80" : "w-0"
              }`}
            >
              {renderRightPanel()}
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="flex sm:hidden flex-1 flex-col min-h-0">
            <div className="px-3 py-2.5 border-b bg-card flex items-center gap-2 shrink-0">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-semibold truncate">{selectedDimDisplay}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium leading-none shrink-0">
                    {currentDimData?.mode}
                  </span>
                  {pendingDimKeys.has(selectedDim) && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-medium leading-none shrink-0">
                      dim edited
                    </span>
                  )}
                  {pendingByDim[selectedDim] > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 font-medium leading-none shrink-0">
                      {pendingByDim[selectedDim]} edited
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {dimNodes.length} member{dimNodes.length !== 1 ? "s" : ""}
                </p>
              </div>

              <button
                onClick={() => handleDimEdit(selectedDim, "start")}
                title="Edit dimension"
                className={`p-1.5 rounded-lg border transition-all shrink-0 ${
                  activeDimEditKey === selectedDim
                    ? "bg-primary/10 border-primary/30 text-primary"
                    : "border-border hover:bg-muted text-muted-foreground"
                }`}
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>

              <div className="relative w-[140px] shrink-0">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-7 h-8 text-sm pr-7"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-2 py-1.5">
              <MemberTreeContent
                dimNodes={dimNodes}
                currentDimData={currentDimData}
                normalizedSearch={normalizedSearch}
                editMap={editMap}
                handleEdit={handleEdit}
                pendingKeys={pendingKeys}
              />
            </div>
          </div>
        </>
      )}

      {/* Mobile Floating Save Bar */}
      {totalPendingCount > 0 && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 z-30 px-3 pb-4 pt-2 bg-gradient-to-t from-background via-background/95 to-transparent pointer-events-none">
          <div className="pointer-events-auto flex items-center gap-2 bg-card border border-amber-200 dark:border-amber-800/60 rounded-2xl px-3 py-2.5 shadow-xl shadow-amber-900/10">
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
              <span className="text-xs font-medium text-amber-700 dark:text-amber-400 truncate">
                {totalPendingCount} unsaved change{totalPendingCount > 1 ? "s" : ""}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDiscardAll}
              disabled={saving}
              className="h-8 px-2.5 text-xs text-muted-foreground shrink-0"
            >
              <XIcon className="w-3 h-3 mr-1" /> Discard
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="h-8 px-3 text-xs bg-amber-600 hover:bg-amber-700 text-white border-0 shrink-0"
            >
              {saving
                ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                : <Save className="w-3 h-3 mr-1" />
              }
              Save
            </Button>
          </div>
        </div>
      )}

      {/* Unsaved Switch Dialog — handles both member & dim */}
      <UnsavedDialog
        open={!!confirmSwitch}
        onSave={handleConfirmSave}
        onDiscard={handleConfirmDiscard}
        onCancel={() => setConfirmSwitch(null)}
      />

      {/* Mobile Edit Right Drawer */}
      <RightDrawer
        open={rightPanelOpen}
        onClose={handleRightPanelClose}
      >
        {renderRightPanel()}
      </RightDrawer>

      {/* Mobile Dimension Bottom Sheet */}
      <BottomSheet
        open={dimSheetOpen}
        onClose={() => setDimSheetOpen(false)}
        title={`Dimensions (${dimensions.length})`}
      >
        <div className="py-1">
          {dimensions.map((dim) => {
            const isActive = selectedDim === dim.dimension;
            const dirtyCount = pendingByDim[dim.dimension] || 0;
            const isDimDirty = pendingDimKeys.has(dim.dimension);
            const memberCount = dimMemberCounts[dim.dimension] || 0;
            const displayName = dim.dimension.replace(/_/g, " ");

            return (
              <div
                key={dim.dimension}
                role="button"
                onClick={() => {
                  setSelectedDim(dim.dimension);
                  setSearchTerm("");
                  setDimSheetOpen(false);
                }}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors cursor-pointer ${
                  isActive
                    ? "bg-primary/5 border-l-2 border-primary"
                    : "border-l-2 border-transparent"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1">
                    <span className={`text-sm font-medium truncate ${isActive ? "text-primary" : ""}`}>
                      {displayName}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      {isDimDirty && (
                        <span className="text-[9px] font-bold px-1 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300">
                          dim
                        </span>
                      )}
                      {dirtyCount > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500 text-white">
                          {dirtyCount}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {dim.mode}
                    </span>
                    <span className="text-[10px] text-muted-foreground/60 flex items-center gap-0.5">
                      <Hash className="w-2.5 h-2.5" /> {memberCount}
                    </span>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDimSheetOpen(false);
                    setTimeout(() => handleDimEdit(dim.dimension, "start"), 300);
                  }}
                  className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground shrink-0 mt-0.5"
                  title="Edit dimension"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </BottomSheet>
    </div>
  );
};

export default MappingPage;